import type {
  AppLifecycleAdapter,
  LifecycleFlushHandler,
  LifecycleVisibility,
} from '../interfaces/lifecycle.js';

export function createWebLifecycleAdapter(): AppLifecycleAdapter {
  let visibility: LifecycleVisibility =
    typeof document !== 'undefined' && document.visibilityState === 'hidden'
      ? 'hidden'
      : 'visible';
  const flushHandlers = new Set<LifecycleFlushHandler>();
  const visibilityListeners = new Set<(next: LifecycleVisibility) => void>();

  function notifyVisibility(next: LifecycleVisibility) {
    visibility = next;
    for (const listener of visibilityListeners) listener(next);
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      notifyVisibility(document.visibilityState === 'hidden' ? 'hidden' : 'visible');
    });

    window.addEventListener('pagehide', () => {
      void flushPendingWork();
    });
  }

  async function flushPendingWork(): Promise<void> {
    for (const handler of [...flushHandlers]) {
      await handler();
    }
  }

  return {
    get visibility() {
      return visibility;
    },

    registerFlushHandler(handler: LifecycleFlushHandler) {
      flushHandlers.add(handler);
      return () => flushHandlers.delete(handler);
    },

    flushPendingWork,

    onVisibilityChange(listener: (next: LifecycleVisibility) => void) {
      visibilityListeners.add(listener);
      return () => visibilityListeners.delete(listener);
    },
  };
}
